<script>
import {defineComponent, onMounted} from 'vue'
import {useCompareStore} from "@js/store/CompareStore.js"
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue"
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue"
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue"
import {Link} from "@inertiajs/vue3"
import IconTrash from "@js/components/Icons/IconTrash.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";

export default defineComponent({
    name: "CompareProductsPage",
    components: {OnlyMobile, OnlyDesktop, IconTrash, Link, NumberFormatComponent, MobileTopBar, Breadcrumbs},
    methods: {
        toggleCompare(id) {
            this.compareStore.toggleCompare(id)
            this.compareStore.getCompares()
            this.compareStore.getComparedProductsData()
        }
    },
    setup() {
        const compareStore = useCompareStore()
        onMounted(() => {
            compareStore.getCompares()
            compareStore.getComparedProductsData()
        })
        return {compareStore}
    }
})
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>
        <h1 class="text-2xl font-bold mb-6">Сравнение товаров</h1>
    </div>
    <div class="sm:container">
        <div v-if="compareStore.loading_products" class="flex animate-pulse space-x-4 p-4">
            <!-- Правые столбцы skeleton -->
            <div class="flex-1 overflow-auto">
                <div class="flex space-x-4">
                    <OnlyDesktop :maxSize="1024">
                        <div v-for="n in 5" :key="n" class="w-64 space-y-4 border border-gray-200 p-4 rounded-lg">
                            <div class="h-24 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                        </div>
                    </OnlyDesktop>
                    <OnlyMobile :maxSize="1024">
                        <div v-for="n in 2" :key="n" class="w-64 space-y-4 border border-gray-200 p-4 rounded-lg">
                            <div class="h-24 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                            <div class="h-6 bg-gray-200 rounded"></div>
                        </div>
                    </OnlyMobile>
                </div>
            </div>
        </div>
        <div v-else>
            <div v-if="!compareStore.products.length" class="text-center py-10 text-gray-500">
                Нет товаров для сравнения.
            </div>
            <div v-else class="overflow-x-auto border border-gray-200 md:rounded-lg">
                <table class="table-fixed min-w-max border-collapse">
                    <thead>
                    <tr>
                        <!-- Пустая зафиксированная ячейка -->
                        <th class="sticky left-0 bg-white z-1 px-4 py-2"></th>
                        <th v-for="product in compareStore.products" :key="product.id"
                            class="relative px-4 py-2 text-center border-l border-gray-200">
                            <button @click.prevent="toggleCompare(product.id)"
                                    class="absolute top-2 right-2 text-gray-400 md:hover:text-red-600">
                                <IconTrash class="w-5 h-5"/>
                            </button>
                            <Link :href="route('product', {slug: product.slug})" class="group block">
                                <img :src="product.image_urls[0]" alt=""
                                     class="h-24 mx-auto mb-2 object-cover"/>
                                <div class="max-md:text-sm font-semibold truncate group-hover:text-navHoverColor">
                                    {{ product.title }}
                                </div>
                                <div class="md:text-lg font-bold mt-1">
                                    <NumberFormatComponent :number="product.price" append_text=" ₽/м²"/>
                                </div>
                            </Link>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <!-- Артикул -->
                    <tr class="bg-gray-50">
                        <th class="max-md:text-xs text-sm sticky left-0 bg-gray-50 z-1 px-4 py-2 text-left font-medium">
                            Артикул
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_art'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.article ?? '—' }}
                        </td>
                    </tr>
                    <!-- Бренд -->
                    <tr>
                        <th class="max-md:text-xs text-sm sticky left-0 bg-white z-1 px-4 py-2 text-left font-medium">
                            Бренд
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_brand'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.brands_title }}
                        </td>
                    </tr>
                    <!-- Страна -->
                    <tr class="bg-gray-50">
                        <th class="max-md:text-xs text-sm sticky left-0 bg-gray-50 z-1 px-4 py-2 text-left font-medium">
                            Страна
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_country'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.countries_title }}
                        </td>
                    </tr>
                    <!-- Размер -->
                    <tr>
                        <th class="max-md:text-xs text-sm sticky left-0 bg-white z-1 px-4 py-2 text-left font-medium">
                            Размер (см)
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_size'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.length }} × {{ product.width }}
                        </td>
                    </tr>
                    <!-- Количество в коробке -->
                    <tr class="bg-gray-50">
                        <th class="max-md:text-xs text-sm sticky left-0 bg-gray-50 z-1 px-4 py-2 text-left font-medium">
                            Количество в коробке
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_count'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.count_in_box }}
                        </td>
                    </tr>
                    <!-- Цвет -->
                    <tr>
                        <th class="max-md:text-xs text-sm sticky left-0 bg-white z-1 px-4 py-2 text-left font-medium">
                            Цвет
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_color'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            <span v-for="color in product.colors" :key="color.id"
                                  :style="{ backgroundColor: color.hex }"
                                  class="inline-block w-4 h-4 rounded-full border mx-1" :title="color.title"></span>
                        </td>
                    </tr>
                    <!-- Поверхность -->
                    <tr class="bg-gray-50">
                        <th class="max-md:text-xs text-sm sticky left-0 bg-gray-50 z-1 px-4 py-2 text-left font-medium">
                            Поверхность
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_surface'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.surfaces.map(s => s.title).join(', ') }}
                        </td>
                    </tr>
                    <!-- Назначение -->
                    <tr>
                        <th class="max-md:text-xs text-sm sticky left-0 bg-white z-1 px-4 py-2 text-left font-medium">
                            Назначение
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_purpose'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.purposes.map(pu => pu.title).join(', ') }}
                        </td>
                    </tr>
                    <!-- Помещения -->
                    <tr class="bg-gray-50">
                        <th class="max-md:text-xs text-sm sticky left-0 bg-gray-50 z-1 px-4 py-2 text-left font-medium">
                            Помещения
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_rooms'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.rooms.map(r => r.title).join(', ') }}
                        </td>
                    </tr>
                    <!-- Узор -->
                    <tr>
                        <th class="max-md:text-xs text-sm sticky left-0 bg-white z-1 px-4 py-2 text-left font-medium">
                            Узор
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_pattern'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.patterns.map(pt => pt.title).join(', ') }}
                        </td>
                    </tr>
                    <!-- Форма -->
                    <tr class="bg-gray-50">
                        <th class="max-md:text-xs text-sm sticky left-0 bg-gray-50 z-1 px-4 py-2 text-left font-medium">
                            Форма
                        </th>
                        <td v-for="product in compareStore.products" :key="product.id + '_form'"
                            class="max-md:text-xs text-sm px-4 py-2 text-center border-t border-l border-gray-200">
                            {{ product.forms.map(f => f.title).join(', ') }}
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.z-1{
    z-index: 1;
}
</style>
